import { useState } from 'react';
import { Link, useParams, useLocation } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  MessageSquare,
  Plus,
  Pin,
  Users,
  Lock,
  Edit,
  Trash2,
  Flag,
  ThumbsUp,
} from 'lucide-react';
import type { PaginatedResponse } from '@shared/api-contracts';

import { PublicShell } from '@/components/public-shell';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useRegionScope } from '@/hooks/useRegionScope';
import { useToast } from '@/hooks/use-toast';
import { clearPersistedGeography } from '@/lib/geography';
import { redirectToLogin } from '@/lib/authUtils';
import { formatDateCompact } from '@/lib/dateUtils';
import { apiRequest, fetchJson, queryClient } from '@/lib/queryClient';
import { RichTextEditor, RichTextDisplay } from '@/components/rich-text-editor';
import { AutoRedirect } from '@/components/auto-redirect';

const EDIT_WINDOW_MS = 15 * 60 * 1000;

interface CommunityTopicListItem {
  id: string;
  title: string;
  content: string | null;
  isSticky: boolean;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string;
  replyCount: number;
  likeCount: number;
  userLiked: boolean;
  user: { firstName: string | null; lastName: string | null; profileImageUrl: string | null };
}

interface TopicReply {
  id: string;
  content: string;
  isDeleted: boolean;
  userId: string;
  createdAt: string;
  updatedAt: string;
  userFirstName: string | null;
  userLastName: string | null;
  userProfileImageUrl: string | null;
  likeCount: number;
  userLiked: boolean;
}

interface TopicDetail {
  topic: {
    id: string;
    title: string;
    content: string | null;
    isSticky: boolean;
    isLocked: boolean;
    userId: string;
    createdAt: string;
    updatedAt: string;
    userFirstName: string | null;
    userLastName: string | null;
    userProfileImageUrl: string | null;
    likeCount: number;
    userLiked: boolean;
  };
  replies: PaginatedResponse<TopicReply>;
}

function authorName(first: string | null, last: string | null) {
  return [first, last].filter(Boolean).join(' ') || 'Kalovate member';
}

function initials(first: string | null, last: string | null) {
  return (
    [first, last]
      .filter(Boolean)
      .map(n => n![0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'K'
  );
}

function canEdit(createdAt: string) {
  return Date.now() - new Date(createdAt).getTime() < EDIT_WINDOW_MS;
}

function TopicDetailView({
  topicId,
  onBack,
  currentUserId,
  isAdmin,
}: {
  topicId: string;
  onBack: () => void;
  currentUserId: string | undefined;
  isAdmin: boolean;
}) {
  const { toast } = useToast();
  const [replyContent, setReplyContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ topicId?: string; replyId?: string } | null>(
    null
  );
  const [reportReason, setReportReason] = useState('');

  const { data, isLoading } = useQuery<TopicDetail>({
    queryKey: ['/community/topics', topicId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/community/topics/${topicId}`);
      return res.json();
    },
  });

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      await apiRequest('POST', `/api/community/topics/${topicId}/replies`, { content });
    },
    onSuccess: () => {
      setReplyContent('');
      queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to post reply', variant: 'destructive' }),
  });

  const editReplyMutation = useMutation({
    mutationFn: async ({ replyId, content }: { replyId: string; content: string }) => {
      await apiRequest('PUT', `/api/community/topics/${topicId}/replies/${replyId}`, { content });
    },
    onSuccess: () => {
      setEditingReplyId(null);
      queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to edit reply', variant: 'destructive' }),
  });

  const deleteReplyMutation = useMutation({
    mutationFn: async (replyId: string) => {
      await apiRequest('DELETE', `/api/community/topics/${topicId}/replies/${replyId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] }),
    onError: () => toast({ title: 'Failed to delete reply', variant: 'destructive' }),
  });

  const adminDeleteReplyMutation = useMutation({
    mutationFn: async (replyId: string) => {
      await apiRequest('DELETE', `/api/admin/community/replies/${replyId}`);
    },
    onSuccess: () => {
      toast({ title: 'Reply removed' });
      queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] });
    },
    onError: () => toast({ title: 'Failed to remove reply', variant: 'destructive' }),
  });

  const stickyMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/community/topics/${topicId}/sticky`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] }),
    onError: () => toast({ title: 'Failed to toggle sticky', variant: 'destructive' }),
  });

  const lockMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/community/topics/${topicId}/lock`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] }),
    onError: () => toast({ title: 'Failed to toggle lock', variant: 'destructive' }),
  });

  const reportMutation = useMutation({
    mutationFn: async (payload: { topicId?: string; replyId?: string; reason: string }) => {
      await apiRequest('POST', '/api/community/reports', payload);
    },
    onSuccess: () => {
      toast({ title: 'Report submitted', description: 'Thank you — our team will review it.' });
      setReportDialogOpen(false);
      setReportReason('');
    },
    onError: (err: Error) => {
      toast({ title: 'Report failed', description: err.message, variant: 'destructive' });
    },
  });

  const likeTopicMutation = useMutation({
    mutationFn: async ({ liked }: { liked: boolean }) => {
      const method = liked ? 'DELETE' : 'POST';
      await apiRequest(method, `/api/community/topics/${topicId}/like`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] }),
  });

  const likeReplyMutation = useMutation({
    mutationFn: async ({ replyId, liked }: { replyId: string; liked: boolean }) => {
      const method = liked ? 'DELETE' : 'POST';
      await apiRequest(method, `/api/community/topics/${topicId}/replies/${replyId}/like`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/community/topics', topicId] }),
  });

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Button variant="ghost" onClick={onBack} className="gap-2 pl-0">
          <ChevronLeft className="h-4 w-4" /> Back to discussions
        </Button>
        <div className="h-48 animate-pulse rounded-[1.75rem] border border-border/70 bg-card" />
      </div>
    );
  }

  if (!data) return null;

  const { topic, replies } = data;
  const isTopicAuthor = currentUserId === topic.userId;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="gap-2 pl-0">
        <ChevronLeft className="h-4 w-4" /> Back to discussions
      </Button>

      {/* Topic */}
      <article className="rounded-[1.75rem] border border-border/70 bg-card p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {topic.isSticky && (
            <Badge variant="secondary" className="rounded-full px-3 py-1">
              <Pin className="mr-1 h-3 w-3" /> Pinned
            </Badge>
          )}
          {topic.isLocked && (
            <Badge variant="outline" className="rounded-full px-3 py-1">
              <Lock className="mr-1 h-3 w-3" /> Locked
            </Badge>
          )}
        </div>

        <h2 className="text-3xl font-semibold tracking-tight">{topic.title}</h2>
        <RichTextDisplay
          html={topic.content}
          className="mt-4 text-sm leading-6 text-muted-foreground"
        />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Avatar className="h-8 w-8">
              <AvatarImage src={topic.userProfileImageUrl ?? undefined} />
              <AvatarFallback>{initials(topic.userFirstName, topic.userLastName)}</AvatarFallback>
            </Avatar>
            <span>{authorName(topic.userFirstName, topic.userLastName)}</span>
            <span className="text-xs">· {formatDateCompact(topic.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => stickyMutation.mutate()}
                  disabled={stickyMutation.isPending}
                >
                  {topic.isSticky ? 'Unpin' : 'Pin'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => lockMutation.mutate()}
                  disabled={lockMutation.isPending}
                >
                  {topic.isLocked ? 'Unlock' : 'Lock'}
                </Button>
              </>
            )}
            {currentUserId && (
              <Button
                size="sm"
                variant="ghost"
                className={`gap-1 ${topic.userLiked ? 'text-primary' : 'text-muted-foreground'}`}
                onClick={() => likeTopicMutation.mutate({ liked: topic.userLiked })}
                disabled={likeTopicMutation.isPending}
                aria-label={topic.userLiked ? 'Unlike' : 'Like'}
              >
                <ThumbsUp className="h-3.5 w-3.5" />
                {topic.likeCount > 0 && topic.likeCount}
              </Button>
            )}
            {!isTopicAuthor && currentUserId && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 text-muted-foreground"
                onClick={() => {
                  setReportTarget({ topicId: topic.id });
                  setReportDialogOpen(true);
                }}
              >
                <Flag className="h-3.5 w-3.5" /> Report
              </Button>
            )}
          </div>
        </div>
      </article>

      {/* Replies */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          {replies.total} {replies.total === 1 ? 'reply' : 'replies'}
        </h3>

        {replies.data.map(reply => {
          const isAuthor = currentUserId === reply.userId;
          const withinWindow = canEdit(reply.createdAt);

          if (editingReplyId === reply.id) {
            return (
              <div
                key={reply.id}
                className="rounded-[1.25rem] border border-border/70 bg-card p-4 space-y-3"
              >
                <RichTextEditor
                  value={editContent}
                  onChange={setEditContent}
                  placeholder="Edit your reply…"
                  minHeight="80px"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      editReplyMutation.mutate({ replyId: reply.id, content: editContent })
                    }
                    disabled={editReplyMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingReplyId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div key={reply.id} className="rounded-[1.25rem] border border-border/70 bg-card p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={reply.userProfileImageUrl ?? undefined} />
                    <AvatarFallback>
                      {initials(reply.userFirstName, reply.userLastName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium">
                    {authorName(reply.userFirstName, reply.userLastName)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    · {formatDateCompact(reply.createdAt)}
                  </span>
                  {reply.updatedAt !== reply.createdAt && (
                    <span className="text-xs text-muted-foreground">(edited)</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isAuthor && withinWindow && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Edit reply"
                      onClick={() => {
                        setEditingReplyId(reply.id);
                        setEditContent(reply.content);
                      }}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isAuthor && withinWindow && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      aria-label="Delete reply"
                      onClick={() => deleteReplyMutation.mutate(reply.id)}
                      disabled={deleteReplyMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isAdmin && !isAuthor && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      aria-label="Admin remove reply"
                      onClick={() => adminDeleteReplyMutation.mutate(reply.id)}
                      disabled={adminDeleteReplyMutation.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {currentUserId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-7 px-2 gap-1 ${reply.userLiked ? 'text-primary' : 'text-muted-foreground'}`}
                      onClick={() =>
                        likeReplyMutation.mutate({ replyId: reply.id, liked: reply.userLiked })
                      }
                      disabled={likeReplyMutation.isPending}
                      aria-label={reply.userLiked ? 'Unlike reply' : 'Like reply'}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                      {reply.likeCount > 0 && <span className="text-xs">{reply.likeCount}</span>}
                    </Button>
                  )}
                  {!isAuthor && currentUserId && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground"
                      aria-label="Report reply"
                      onClick={() => {
                        setReportTarget({ replyId: reply.id });
                        setReportDialogOpen(true);
                      }}
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <RichTextDisplay
                html={reply.content}
                className="text-sm leading-6 text-muted-foreground"
              />
            </div>
          );
        })}
      </div>

      {/* Reply form */}
      {!topic.isLocked && currentUserId && (
        <div className="space-y-3">
          <RichTextEditor
            value={replyContent}
            onChange={setReplyContent}
            placeholder="Write a reply…"
            minHeight="80px"
          />
          <Button
            onClick={() => replyMutation.mutate(replyContent)}
            disabled={replyContent.trim().length < 5 || replyMutation.isPending}
            className="rounded-full px-5"
          >
            {replyMutation.isPending ? 'Posting…' : 'Post reply'}
          </Button>
        </div>
      )}
      {topic.isLocked && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Lock className="h-4 w-4" /> This discussion is locked and no longer accepting replies.
        </p>
      )}

      {/* Report dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Describe why this content violates community guidelines…"
              rows={4}
            />
            <Button
              onClick={() => reportMutation.mutate({ ...reportTarget!, reason: reportReason })}
              disabled={reportReason.trim().length < 3 || reportMutation.isPending}
              className="w-full"
            >
              {reportMutation.isPending ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function RegionCommunity() {
  const { regionSlug, localitySlug } = useParams<{ regionSlug: string; localitySlug?: string }>();
  const [, navigate] = useLocation();
  const { region, locality, isLoading, isError } = useRegionScope(regionSlug, localitySlug);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  const routeKey = localitySlug ? 'locality-community' : 'region-community';
  const apiBase = localitySlug
    ? `/api/regions/${regionSlug}/${localitySlug}`
    : `/api/regions/${regionSlug}`;

  const { data: response, isLoading: topicsLoading } = useQuery<
    PaginatedResponse<CommunityTopicListItem>
  >({
    queryKey: ['region-community', regionSlug, localitySlug],
    queryFn: () => fetchJson<PaginatedResponse<CommunityTopicListItem>>(`${apiBase}/community`),
    enabled: !!regionSlug && !isError,
    staleTime: 1000 * 30,
  });

  const createTopicMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('POST', `${apiBase}/community`, {
        title: title.trim(),
        content: content.trim(),
      });
    },
    onSuccess: async () => {
      setTitle('');
      setContent('');
      setShowDialog(false);
      await queryClient.invalidateQueries({
        queryKey: ['region-community', regionSlug, localitySlug],
      });
      toast({
        title: 'Discussion created',
        description: 'Your topic is now live in this community.',
      });
    },
    onError: () => {
      toast({
        title: 'Unable to create topic',
        description: 'Check the title and content, then try again.',
        variant: 'destructive',
      });
    },
  });

  const topics = response?.data ?? [];
  const canSubmit = title.trim().length > 0 && !createTopicMutation.isPending;

  if (isLoading) {
    return (
      <PublicShell routeKey={routeKey}>
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (isError || !region) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell-narrow flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <p className="editorial-kicker">Community unavailable</p>
          <h1 className="text-4xl font-semibold">We couldn't find that community.</h1>
          <p className="section-copy">
            The link may be out of date, or this geography is no longer active.
          </p>
          <AutoRedirect />
          <Button
            className="rounded-full px-5"
            onClick={() => {
              clearPersistedGeography();
              navigate('/');
            }}
          >
            Choose another region
          </Button>
        </div>
      </PublicShell>
    );
  }

  const displayName = locality?.name ?? region.name;
  const basePath = locality
    ? `/regions/${region.slug}/${locality.slug}`
    : `/regions/${region.slug}`;
  const isAdmin = user?.role === 'admin';

  function handleStartDiscussion() {
    if (!isAuthenticated) {
      redirectToLogin(500);
      return;
    }
    setShowDialog(true);
  }

  return (
    <PublicShell routeKey={routeKey}>
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell grid gap-8 py-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <Link
              href={basePath}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to {displayName}
            </Link>
            <p className="editorial-kicker mt-6">Public community</p>
            <h1 className="section-title mt-4">{displayName} discussions</h1>
            <p className="section-copy mt-5 max-w-2xl">
              Ask questions, share recommendations, and connect with people in your area. Open to
              everyone — no enrollment required.
            </p>
            {!selectedTopicId && (
              <div className="mt-8">
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                  <Button
                    className="touch-target rounded-full px-5"
                    onClick={handleStartDiscussion}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Start a discussion
                  </Button>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>New discussion for {displayName}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <Input
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Topic title"
                      />
                      <RichTextEditor
                        value={content}
                        onChange={setContent}
                        placeholder="What do you want to talk about?"
                        minHeight="140px"
                      />
                      <Button
                        onClick={() => createTopicMutation.mutate()}
                        disabled={!canSubmit}
                        className="w-full"
                      >
                        {createTopicMutation.isPending ? 'Posting...' : 'Post discussion'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>

          <Card className="glass-panel rounded-[2rem] border-border/60 shadow-lg">
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {topics.length} open topics
                </Badge>
                <Badge variant="outline" className="rounded-full px-3 py-1">
                  {locality ? locality.name : region.name}
                </Badge>
              </div>
              <div className="flex items-center gap-3 rounded-[1.4rem] border border-border/70 bg-background/80 p-4">
                <Users className="h-5 w-5 text-primary" />
                <p className="text-sm leading-6 text-muted-foreground">
                  These discussions are visible to everyone. Enrollment is not required to read or
                  participate.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell space-y-5">
          {selectedTopicId ? (
            <TopicDetailView
              topicId={selectedTopicId}
              onBack={() => setSelectedTopicId(null)}
              currentUserId={user?.id}
              isAdmin={isAdmin}
            />
          ) : topicsLoading ? (
            <div className="grid gap-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-36 animate-pulse rounded-[1.75rem] border border-border/70 bg-card"
                />
              ))}
            </div>
          ) : topics.length > 0 ? (
            <div className="grid gap-5">
              {topics.map(topic => {
                const author = authorName(topic.user.firstName, topic.user.lastName);
                const ini = initials(topic.user.firstName, topic.user.lastName);

                return (
                  <article
                    key={topic.id}
                    className="cursor-pointer rounded-[1.75rem] border border-border/70 bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
                    onClick={() => setSelectedTopicId(topic.id)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {topic.isSticky && (
                        <Badge variant="secondary" className="rounded-full px-3 py-1">
                          <Pin className="mr-1 h-3 w-3" /> Pinned
                        </Badge>
                      )}
                      {topic.isLocked && (
                        <Badge variant="outline" className="rounded-full px-3 py-1">
                          <Lock className="mr-1 h-3 w-3" /> Locked
                        </Badge>
                      )}
                      <Badge variant="outline" className="rounded-full px-3 py-1">
                        <MessageSquare className="mr-1 h-3 w-3" />
                        {topic.replyCount} {topic.replyCount === 1 ? 'reply' : 'replies'}
                      </Badge>
                      {topic.likeCount > 0 && (
                        <Badge variant="outline" className="rounded-full px-3 py-1">
                          <ThumbsUp className="mr-1 h-3 w-3" />
                          {topic.likeCount}
                        </Badge>
                      )}
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight">{topic.title}</h2>
                    <p className="mt-3 line-clamp-2 text-sm leading-6 text-muted-foreground">
                      {topic.content
                        ? topic.content
                            .replace(/<[^>]+>/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim()
                        : ''}
                    </p>
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                      <div className="inline-flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={topic.user.profileImageUrl ?? undefined} />
                          <AvatarFallback>{ini}</AvatarFallback>
                        </Avatar>
                        <span>{author}</span>
                      </div>
                      <span>{formatDateCompact(topic.createdAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
              <CardContent className="space-y-4 p-8 text-center">
                <p className="editorial-kicker">No discussions yet</p>
                <h2 className="text-3xl font-semibold">
                  Start the first thread for {displayName}.
                </h2>
                <p className="mx-auto max-w-2xl text-sm leading-6 text-muted-foreground">
                  Ask for recommendations, coordinate a meetup, or share a local tip.
                </p>
                <div>
                  <Button className="rounded-full px-5" onClick={handleStartDiscussion}>
                    <Plus className="mr-2 h-4 w-4" /> Start a discussion
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
